import type { BlockNode, DocumentV3, PageV3, TemplateDef, Theme } from '@docgen/schema';
import { BCE_DATA } from './data.js';

/**
 * The Avis proposal as a Model-v3 BLOCK document, mirroring
 * `reference/bce-proposal-mockup.html` page for page — no per-template code.
 * Every page below is only palette blocks + bindings, which is the product's
 * proof: a template designer could assemble this by drag-and-drop. The same
 * page block-trees are exported as TemplateDefs (palette presets).
 */

export const BCE_THEME_V3: Theme = {
  colors: {
    navy: '#0e2e77',
    royal: '#1851d1',
    'royal-dark': '#103589',
    sky: '#7da6ff',
    blue: '#4765a0',
    yellow: '#ffc72c',
    'yellow-dark': '#e5a900',
    mist: '#f5f7fa',
    line: '#dfe5ef',
    body: '#33415c',
  },
  baseCss: `
*{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
html,body{margin:0;padding:0}
body{font-family:'Geist',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;color:var(--body);font-size:11pt;line-height:1.55;background:#8894ad}
.page{width:8.5in;min-height:11in;margin:0 auto 18px;padding:0.72in 0.78in;background:#fff;position:relative;overflow:hidden;display:flex;flex-direction:column}
@page{size:letter;margin:0}
@media print{body{background:#fff}.page{margin:0;box-shadow:none;page-break-after:always;min-height:auto;height:11in}.page:last-child{page-break-after:auto}}
`,
};

// `steps` is the v1/v2 Next Steps copy (it names the sample's date); the v3
// page binds `nextSteps` below, so the v3 data leaves it out.
const BASE_V3: Record<string, unknown> = Object.fromEntries(Object.entries(BCE_DATA).filter(([key]) => key !== 'steps'));

/** The mockup's data tree: shared base plus the pricing/toc/values copy. */
const DATA_V3: Record<string, unknown> = {
  ...BASE_V3,
  // The client contact the introduction greets. bcecloud fills it from the
  // opportunity's contact role.
  contact: { firstName: '', name: '' },
  // Derived on every save from the proposal's own facts (see
  // apps/api lib/proposals/derived-content.ts); these are the neutral defaults
  // shown before the first save. `note` / `customHeading` are the rep's own
  // introduction and heading, used instead of the default when filled in.
  intro: {
    heading: 'Your event proposal',
    html: '<p>Hi there,</p><p>Thank you for considering Best Corporate Events for your team. This proposal covers the program we recommend for your group on your event date.</p><p>The pages that follow describe the program and lay out the full pricing for your group.</p><p>If you have any questions, call or email me directly.</p>',
    note: '',
    customHeading: '',
  },
  labels: { preparedFor: '', eventLine: '', nextStepsHeading: 'Locking in your date' },
  // `anchor` is the resolved page key each row links to (the Contents list turns
  // it into a jump link in the preview/PDF); bcecloud rewrites this whole array
  // from pricing on every save, keeping the anchors.
  toc: [
    { n: '01', title: 'Introduction & About BEST', desc: "Who you're working with, and how we work", page: '3', anchor: 'intro' },
    { n: '02', title: 'The Program', desc: 'Bike Build Donation® — what your team will actually do', page: '4', anchor: 'program:0' },
    { n: '03', title: 'Your Investment', desc: 'Full pricing for 18 participants and 2 teams', page: '5', anchor: 'investment' },
    { n: '04', title: 'Frequently Asked Questions', desc: 'This program, and running an event with us generally', page: '6', anchor: 'faqs' },
    { n: '05', title: 'Choosing The Right Program', desc: 'Venue, timing and outcome — what to weigh up', page: '7', anchor: 'choosing' },
    { n: '06', title: 'Next Steps', desc: 'How to confirm and lock in your date', page: '8', anchor: 'next-steps' },
  ],
  // The Investment page binds to these; bcecloud rewrites them from the
  // proposal's numeric pricing on every save (see apps/api lib/proposals).
  // The Investment page repeats over `pricing.options`; bcecloud rewrites these
  // from the proposal's numeric pricing on every save (see apps/api lib/proposals).
  pricing: {
    validUntil: '2026-09-14T12:00:00Z',
    final: 6205,
    optionCount: 1,
    participants: 18,
    validityText: 'Pricing valid until September 14, 2026, and subject to change thereafter.',
    options: [
      {
        id: 'opt-1',
        name: 'Option 1',
        headline: 'Option 1 — for 18 participants',
        net: 6205,
        participants: 18,
        // No discount by default — the savings callout repeats over this, so it
        // stays hidden until a real discount is added.
        savings: [],
        rows: [
          {
            label: 'Bike Build Donation®',
            note: '18 participants × $344.72 per participant',
            amount: '$6,205.00',
            kind: 'normal',
          },
        ],
      },
    ],
  },
  values: [
    { n: '1', title: 'Client partnership', body: 'Your people are at the center of everything we plan.' },
    { n: '2', title: 'Team first', body: 'Our coworkers are our closest allies and most relied-upon partners.' },
    { n: '3', title: 'Innovation & growth', body: 'We keep learning, adapting and improving the work.' },
    { n: '4', title: 'Integrity', body: 'We do the right thing — even when no one is watching.' },
    { n: '5', title: 'Excellence & accountability', body: 'We take pride in the work and own the outcome.' },
  ],
  checklists: [
    {
      title: 'Your venue',
      items: [
        { text: 'Can they receive shipped boxes or pallets four to five days ahead? Ask us the quantity.' },
        { text: 'Is there secure indoor storage, and can pallets be kept without breaking them down?' },
        { text: 'Can they move materials in and out of the event room?' },
        { text: 'Is the room large enough for the activity? Each program page lists the space it needs per person.' },
        { text: "If you're outdoors, is the space reserved and does it need a permit?" },
      ],
    },
    {
      title: 'Structure and outcome',
      items: [
        { text: 'Does your agenda allow the full program length, plus setup and breakdown?' },
        { text: 'Programs can be shortened or extended — shorter versions drop some elements, longer ones add team-building depth.' },
        { text: 'Tell us about your participants, your goals and what drew you to this program.' },
        { text: 'Will anything need a food & beverage manager or an A/V vendor?' },
      ],
    },
    {
      title: 'Charity partnership',
      items: [
        { text: "The more flexible you are on the receiving charity, the more likely we finalize an arrangement everyone's happy with." },
        { text: "Nominating your own charity is welcome — you'd coordinate their attendance and donation pickup." },
      ],
    },
  ],
  nextSteps: [
    { n: '01', title: 'Confirm the date', desc: "Reply or call and I'll hold your date while we finalize details." },
    { n: '02', title: 'Return the signed contract', desc: 'Sign and send it back to lock in your date and pricing.' },
    { n: '03', title: 'We plan the details', desc: 'We confirm the venue setup, timing and any charity partner with you once the contract is signed.' },
    { n: '04', title: 'Materials ship ahead', desc: 'Program materials arrive at your venue ahead of the event.' },
  ],
  // The general FAQs, word for word as the S-Docs proposal templates carry them.
  generalFaqs: [
    { q: "Should we determine team composition prior to the event?", a: "Yes, determining the team makeup prior to the event ensures individuals you would like interacting with each other will have the opportunity to bond and network. However, if desired the facilitator can randomly form the teams as part of our fun and energizing kick-off." },
    { q: "Do you recommend we offer prizes? Any suggestions?", a: "We award gold medals to all members of the winning team. You are welcome to bring additional prizes." },
    { q: "How much time is needed for setup and breakdown?", a: "For most programs, 1 to 2 hours is ideal for setup. For exceptionally large groups or material-heavy programs, more time will be required. If a more expedited setup is required, it may be possible to prep materials by utilizing a separate staging area near the event space. Breakdown typically requires 30 minutes to an hour. This may vary based on group size and venue characteristics. Your sales representative and/or program manager can provide specifics based on your chosen program and venue." },
    { q: "Can drinks and/or food be served?", a: "Yes, drinks and/or finger foods work well for most programs. However, a full sit-down meal during the program is not recommended in most cases." },
    { q: "Who provides A/V components?", a: "You will need to provide a wireless microphone, sound system, and related technical support (only required for groups over 50). These items are typically available through your venue." },
    { q: "Who identifies and coordinates with the charity for CSR programs?", a: "We will identify and coordinate with the receiving charity once we receive your signed contract. We are happy to work with a charity you identify, if you confirm that they can accommodate the donation and can come to receive it at your event." },
  ],
  programs: [
    {
      name: 'Bike Build Donation®',
      price: 6205,
      image: '',
      category: 'Charitable',
      // The program page's projected shape: a 0-or-1 image gallery and give-back
      // list the page repeats over (bcecloud rewrites these on every save).
      gallery: [],
      donation: [{ headline: 'Giving back: 4 bikes', body: '2 per team · includes helmet and lock' }],
      faqSection: [{ count: 4 }],
      specs: [
        { value: '10', label: 'Per team' },
        { value: '1.5–2 hrs', label: 'Duration' },
        { value: 'Low', label: 'Intensity' },
        { value: 'Either', label: 'Indoor / outdoor' },
        { value: '25 sq ft', label: 'Per person' },
      ],
      descriptionHtml:
        '<p>Bike Build Donation® is one of our most popular give-back programs. Participants feel the impact of assembling and donating bicycles to children who need them — using organization, delegation, communication and a little mechanical skill along the way.</p><p>The program opens with an icebreaker to get everyone "in gear", then a walkthrough from your lead facilitator. Teams of 10 compete in our browser-based Brainstorm app — trivia, puzzles and photo/video challenges, run from their own phones, nothing to download. As teams earn points they "purchase" parts from the on-site bike shop and start building. <strong>Every 20-inch bicycle your group builds comes with a helmet and a lock.</strong></p><p>At the end, a charity representative comes to receive the bikes in person and thank your team. It is, reliably, the part people talk about afterwards.</p>',
      faqs: [
        { q: 'How should the room be set up?', a: 'A round table and chairs per team, plus two six- or eight-foot tables at the front.' },
        { q: 'Who gets the bikes to the charity?', a: "Where the charity can't collect, we arrange transport from your venue and bill it as an additional cost." },
        { q: 'What happens at the end?', a: 'A charity representative attends to receive the donation and thank your team. Our preferred partner, Boys & Girls Clubs of America, also has a short film we can play.' },
        { q: 'Can we donate larger bikes?', a: 'Yes — charities often ask for them. Any or all bikes can be upgraded to cruisers for $185 per bike.' },
      ],
    },
  ],
};

const FOOTER_LEFT = 'Best Corporate Events & TeamBuilding®';
const footer = (id: string, right: string | { $bind: string }): BlockNode => ({
  id, type: 'pageFooter', props: { left: FOOTER_LEFT, right },
});
/**
 * Deal-specific copy is never literal in the pages: it binds to data that
 * bcecloud derives from the proposal's own facts on every save
 * (apps/api lib/proposals/derived-content.ts). `template-guard.test.ts` fails
 * on any literal that looks like a person, city, date or count.
 */
const PREPARED_FOR = { $bind: 'labels.preparedFor' };
const grow = (id: string): BlockNode => ({
  id, type: 'spacer', props: { size: 20 }, style: { grow: true },
});

const coverBlocks: BlockNode[] = [
  {
    id: 'cover-decor', type: 'coverDecor', props: {}, style: { justify: 'between', grow: true },
    children: [
      {
        id: 'cover-logo', type: 'logo',
        props: { src: { $bind: 'brand.logoWhite' }, alt: { $bind: 'brand.name' }, width: 190 },
      },
      {
        id: 'cover-main', type: 'stack', props: { direction: 'column' },
        children: [
          { id: 'cover-eyebrow', type: 'eyebrow', props: { text: 'Event Proposal' }, style: { color: 'sky' } },
          {
            id: 'cover-title', type: 'heading',
            props: { text: { $bind: 'event.title' }, level: '1' }, style: { color: '#fff' },
          },
          { id: 'cover-rule', type: 'divider', props: { variant: 'rule' } },
          { id: 'cover-for', type: 'eyebrow', props: { text: 'Prepared for' }, style: { color: 'sky' } },
          {
            id: 'cover-client', type: 'heading',
            props: { text: { $bind: 'client.name' }, level: '2' },
            style: { color: '#fff', fontSize: '23pt' },
          },
          {
            id: 'cover-meta', type: 'stack', props: { direction: 'row' }, style: { align: 'center', gap: 7 },
            children: [
              { id: 'cover-meta-city', type: 'paragraph', props: { text: { $bind: 'event.city' }, size: 'small' }, style: { color: '#fff' } },
              { id: 'cover-meta-dot1', type: 'paragraph', props: { text: '·', size: 'small' }, style: { color: 'sky' } },
              { id: 'cover-meta-date', type: 'paragraph', props: { text: { $bind: 'event.date', format: 'date' }, size: 'small' }, style: { color: 'sky' } },
              { id: 'cover-meta-dot2', type: 'paragraph', props: { text: '·', size: 'small' }, style: { color: 'sky' } },
              { id: 'cover-meta-part', type: 'paragraph', props: { text: { $bind: 'event.participants' }, size: 'small' }, style: { color: 'sky' } },
              { id: 'cover-meta-partlabel', type: 'paragraph', props: { text: 'participants', size: 'small' }, style: { color: 'sky' } },
            ],
          },
          {
            id: 'cover-prepared', type: 'stack', props: { direction: 'column' },
            children: [
              { id: 'cover-prepared-label', type: 'eyebrow', props: { text: 'Prepared by' }, style: { color: 'sky' } },
              { id: 'cover-prepared-name', type: 'paragraph', props: { text: { $bind: 'owner.name' }, size: 'base' }, style: { color: '#fff' } },
              { id: 'cover-prepared-title', type: 'paragraph', props: { text: { $bind: 'owner.title' }, size: 'small' }, style: { color: 'sky' } },
            ],
          },
        ],
      },
      {
        id: 'cover-foot', type: 'stack', props: { direction: 'row' }, style: { justify: 'between', align: 'end' },
        children: [
          {
            id: 'cover-foot-contact', type: 'stack', props: { direction: 'column' },
            children: [
              { id: 'cover-foot-phone', type: 'paragraph', props: { text: { $bind: 'owner.phone' }, size: 'fine' }, style: { color: 'sky' } },
              { id: 'cover-foot-email', type: 'paragraph', props: { text: { $bind: 'owner.email' }, size: 'fine' }, style: { color: 'sky' } },
            ],
          },
          {
            id: 'cover-foot-brand', type: 'stack', props: { direction: 'column' }, style: { align: 'end' },
            children: [
              { id: 'cover-foot-site', type: 'paragraph', props: { text: { $bind: 'brand.site' }, size: 'fine' }, style: { color: 'sky' } },
              { id: 'cover-foot-tagline', type: 'paragraph', props: { text: { $bind: 'brand.tagline' }, size: 'fine' }, style: { color: 'sky' } },
            ],
          },
        ],
      },
    ],
  },
];

const contentsBlocks: BlockNode[] = [
  {
    id: 'toc-head', type: 'sectionHeader',
    props: { eyebrow: 'Contents', heading: "What's in this proposal" },
  },
  { id: 'toc-list', type: 'tocList', props: { rows: { $bind: 'toc' } } },
  grow('toc-grow'),
  footer('toc-foot', PREPARED_FOR),
];

const introBlocks: BlockNode[] = [
  {
    id: 'intro-head', type: 'sectionHeader',
    props: { eyebrow: 'Introduction', heading: { $bind: 'intro.heading' } },
  },
  { id: 'intro-p1', type: 'richtext', props: { html: { $bind: 'intro.html' } } },
  { id: 'intro-gap', type: 'spacer', props: { size: 26 } },
  {
    id: 'about-head', type: 'sectionHeader',
    props: { eyebrow: 'About BEST', heading: 'Why teams remember this one' },
  },
  {
    id: 'about-mission', type: 'callout',
    props: {
      variant: 'quiet',
      big: '',
      text: 'We create corporate events that achieve real objectives, strengthen relationships and stay memorable. Our programs are tailored to your team, run by experienced facilitators and backed by national charity partnerships.',
    },
  },
  { id: 'about-values', type: 'stepsList', props: { items: { $bind: 'values' } } },
  grow('intro-grow'),
  footer('intro-foot', PREPARED_FOR),
];

const programBlocks: BlockNode[] = [
  {
    id: 'prog-head', type: 'sectionHeader',
    props: { eyebrow: 'The Program', heading: { $bind: 'program.name' } },
  },
  {
    id: 'prog-image', type: 'image',
    repeat: { over: 'program.gallery', as: 'shot' },
    // Natural aspect ratio, capped in height so it never dominates the page.
    props: { src: { $bind: 'shot.src' }, alt: { $bind: 'shot.alt' }, width: '100%', height: '300px' },
    style: { radius: 10 },
  },
  { id: 'prog-specs', type: 'statBar', props: { items: { $bind: 'program.specs' } } },
  { id: 'prog-links', type: 'richtext', repeat: { over: 'program.links', as: 'link' }, props: { html: { $bind: 'link.html' } } },
  { id: 'prog-desc', type: 'richtext', props: { html: { $bind: 'program.descriptionHtml' } } },
  {
    id: 'prog-donation', type: 'callout',
    repeat: { over: 'program.donation', as: 'give' },
    props: { variant: 'accent', big: { $bind: 'give.headline' }, text: { $bind: 'give.body' } },
  },
  { id: 'prog-gap', type: 'spacer', props: { size: 24 } },
  {
    id: 'prog-faq-head', type: 'sectionHeader',
    // Shown only when the program has FAQs; bcecloud projects this 0-or-1 list.
    repeat: { over: 'program.faqSection', as: 'faqSection' },
    props: { eyebrow: 'Good to know', heading: 'About this program' },
  },
  { id: 'prog-faqs', type: 'faqList', props: { items: { $bind: 'program.faqs' }, columns: '1' } },
  grow('prog-grow'),
  footer('prog-foot', { $bind: 'labels.eventLine' }),
];

const investmentBlocks: BlockNode[] = [
  {
    id: 'inv-head', type: 'sectionHeader',
    props: { eyebrow: 'Your Investment', heading: 'Pricing' },
  },
  // One page, a section per option (block-level repeat) — not a page each.
  {
    id: 'inv-options', type: 'stack', props: { direction: 'column' },
    repeat: { over: 'pricing.options', as: 'option' },
    children: [
      { id: 'inv-opt-head', type: 'heading', props: { text: { $bind: 'option.headline' }, level: '3' } },
      {
        id: 'inv-table', type: 'dataTable',
        props: {
          columns: [
            { key: 'label', label: 'Detail', align: 'left' },
            { key: 'amount', label: 'Amount', align: 'right' },
          ],
          rows: { $bind: 'option.rows' },
          totalLabel: 'Net event price',
          totalValue: { $bind: 'option.net', format: 'currency' },
        },
      },
      {
        id: 'inv-savings', type: 'callout',
        repeat: { over: 'option.savings', as: 'saving' },
        props: { variant: 'accent', big: { $bind: 'saving.headline' }, text: { $bind: 'saving.body' } },
      },
      { id: 'inv-opt-gap', type: 'spacer', props: { size: 26 } },
    ],
  },
  {
    id: 'inv-valid', type: 'paragraph',
    props: { text: { $bind: 'pricing.validityText' }, size: 'fine' },
  },
  grow('inv-grow'),
  footer('inv-foot', '(800) 849-8326 · bestcorporateevents.com'),
];

const faqsBlocks: BlockNode[] = [
  {
    id: 'faqs-head', type: 'sectionHeader',
    props: { eyebrow: 'Frequently Asked Questions', heading: 'Running an event with us' },
  },
  { id: 'faqs-list', type: 'faqList', props: { items: { $bind: 'generalFaqs' }, columns: '2' } },
  grow('faqs-grow'),
  footer('faqs-foot', PREPARED_FOR),
];

const choosingBlocks: BlockNode[] = [
  {
    id: 'choose-head', type: 'sectionHeader',
    props: { eyebrow: 'Choosing The Right Program', heading: 'Worth checking before you commit' },
  },
  {
    id: 'choose-intro', type: 'paragraph',
    props: {
      text: "A program fits when the venue, the agenda and the outcome all line up. These are the questions we'd ask on your behalf, and most take a short email to your venue.",
      size: 'base',
    },
  },
  {
    id: 'choose-group', type: 'checklist',
    repeat: { over: 'checklists', as: 'group' },
    props: { title: { $bind: 'group.title' }, items: { $bind: 'group.items' } },
  },
  grow('choose-grow'),
  footer('choose-foot', PREPARED_FOR),
];

const nextStepsBlocks: BlockNode[] = [
  {
    id: 'next-head', type: 'sectionHeader',
    props: { eyebrow: 'Next Steps', heading: { $bind: 'labels.nextStepsHeading' } },
  },
  { id: 'next-list', type: 'tocList', props: { rows: { $bind: 'nextSteps' } } },
  {
    id: 'next-cta', type: 'ctaBand',
    props: {
      heading: 'Ready when you are',
      body: "Say the word and I'll hold your date while we finalize details.",
      buttonLabel: 'Confirm this date',
      buttonHref: { $bind: 'owner.confirmMailto' },
      sigHtml: { $bind: 'owner.signatureHtml' },
    },
  },
  footer('next-foot', '(800) 849-8326 · bestcorporateevents.com'),
];

const PAGES: PageV3[] = [
  { id: 'cover', name: 'Cover', background: 'navy', pad: '0.9in 0.85in', blocks: coverBlocks },
  { id: 'contents', name: 'Contents', blocks: contentsBlocks },
  { id: 'intro', name: 'Introduction & About', blocks: introBlocks },
  {
    id: 'program', name: 'The Program',
    repeat: { over: 'programs', as: 'program' }, blocks: programBlocks,
  },
  {
    // ONE investment page; the `inv-options` stack inside repeats over
    // pricing.options, so every option is a section on this single page. (No
    // page-level repeat — that made a whole page per option.)
    id: 'investment', name: 'Your Investment', blocks: investmentBlocks,
  },
  { id: 'faqs', name: 'FAQs', blocks: faqsBlocks },
  { id: 'choosing', name: 'Choosing The Right Program', blocks: choosingBlocks },
  { id: 'next-steps', name: 'Next Steps', blocks: nextStepsBlocks },
];

export const BCE_SEED_V3: DocumentV3 = {
  version: 3,
  id: 'avis-bike-build',
  name: 'Proposal — Avis Budget Group',
  theme: BCE_THEME_V3,
  data: DATA_V3,
  pages: PAGES,
};

export const BCE_TEMPLATES: TemplateDef[] = PAGES.map((page) => {
  const tpl: TemplateDef = {
    id: `tpl-${page.id}`,
    name: page.name ?? page.id,
    description: `The ${page.name ?? page.id} page from the BCE proposal.`,
    category: 'page',
    blocks: page.blocks,
  };
  if (page.background !== undefined) tpl.background = page.background;
  if (page.pad !== undefined) tpl.pad = page.pad;
  if (page.repeat !== undefined) tpl.repeat = page.repeat;
  return tpl;
});
